"use client";

import { useSignOut } from 'react-firebase-hooks/auth';
import { useTranslation } from 'react-i18next';
import { useFirebaseContext } from '../../lib/context/Firebase.ts';
import { userHasRole } from '../../lib/user-has-role.tsx';
import BuildingSelector from './BuildingSelector';
import { Avatar, Dropdown, DropdownDivider, DropdownHeader, DropdownItem, MegaMenu, Navbar, NavbarBrand, NavbarCollapse, NavbarToggle } from 'flowbite-react';
import LinkItem, { NavItem } from './LinkItem';
import { useAppSelector } from '../../lib/hooks';

export default function Header() {
    const { t } = useTranslation("header");

    // State to manage the navbar's visibility
    const user = useAppSelector(state => state.user.user)
    const firebase = useFirebaseContext()
    const [firebaseSignout] = useSignOut(firebase.auth)
    const selectedBuilding = useAppSelector(state => state.building.building)

    // Toggle function to handle the navbar's display
    function addId(item: { text: string, link: string, condition?: () => boolean }, index: number): NavItem {
        return {
            ...item,
            id: index + 1
        }
    }

    function filterOnCondition(item: Partial<NavItem>): boolean {
        if (item.condition !== undefined) {
            return item.condition()
        }
        return true;
    }

    const buildingSelected = () => selectedBuilding !== undefined
    const loggedIn = () => user != null
    const anonymous = () => user == null
    const hasParking = () => selectedBuilding?.features?.hasParking ?? false
    const loggedInAndBuildingSelected = () => loggedIn() && buildingSelected()
    const loggedInBuildingSelectedAndHasParking = () => loggedIn() && buildingSelected() && hasParking()
    const isAdmin = () => userHasRole(user, ["ADMIN"])
    const isSuperAdmin = () => userHasRole(user, ["SUPER_ADMIN"])
    const superAdminAndBuildingSelected = () => isSuperAdmin() && buildingSelected()

    const navItems = [
        { text: t('Home'), link: '/', condition: anonymous },
        { text: t('signup'), link: '/signup', condition: loggedInAndBuildingSelected },
        { text: t('overview'), link: '/overview', condition: loggedInAndBuildingSelected },
        { text: t('parking'), link: '/parking', condition: loggedInBuildingSelectedAndHasParking },
        { text: t('building'), link: '/building', condition: loggedInAndBuildingSelected },
        { text: t('login'), link: '/login', condition: anonymous },
    ].map(addId).filter(filterOnCondition) as NavItem[]

    const userMenuItems = [
        { text: t('user preferences'), link: "/preferences", condition: () => true },
        { text: t('motd'), link: "/motd", condition: () => true },
        { text: t('monthly-report'), link: "/monthly-report", condition: loggedInAndBuildingSelected },
        { text: t('reservations-list'), link: "/reservations-list", condition: loggedInAndBuildingSelected },
        { text: t('reservations'), link: '/reservations', condition: loggedInAndBuildingSelected },
        { text: t("migration"), link: "/migration", condition: superAdminAndBuildingSelected },
        { text: t("building-admin"), link: "/building-admin", condition: isAdmin },
        { text: t("company-admin"), link: "/company-admin", condition: isAdmin }
    ].map(addId).filter(filterOnCondition) as NavItem[]

    function signOut() {
        firebaseSignout()
            .then(() => {
                window.location.reload();
            })
            .catch((error) => {
                console.error("Error signing out:", error);
            });
    }

    return (
        <MegaMenu className={"lowercase"}>
            <div className="mx-auto flex max-w-screen-xl flex-wrap items-center justify-between lg:p-4 md:space-x-8">
                <Navbar fluid rounded className={"max-w-screen-xl text-base"}>
                    <NavbarBrand href="/" className="pr-20">
                        <img src="/logos/alfa1.svg" alt="alfa1 logo"
                             className="self-center whitespace-nowrap text-xl font-semibold dark:text-white"/>
                    </NavbarBrand>
                    <NavbarToggle/>
                    <NavbarCollapse>
                        <BuildingSelector/>

                        {navItems.map(item => (
                            <LinkItem item={item} key={item.id}/>
                        ))}

                        {(user != null ?
                                (
                                    <div className="flex md:order-2" data-testid="hamburger-menu">
                                        <Dropdown arrowIcon={false} inline label={
                                            <Avatar img={user.photoURL ?? undefined} rounded/>
                                        } className="z-50">
                                            <DropdownHeader>
                                                <span className="block text-sm">{user.displayName}</span>
                                                <span
                                                    className="block truncate text-sm font-medium">{user.email}</span>
                                            </DropdownHeader>

                                            {userMenuItems.map(item => (
                                                <DropdownItem key={item.id}>
                                                    <LinkItem item={item}/>
                                                </DropdownItem>
                                            ))}

                                            <DropdownDivider/>
                                            <DropdownItem
                                                onClick={signOut}>{t("sign out")}</DropdownItem>
                                        </Dropdown>
                                    </div>

                                )
                                : null
                        )}

                    </NavbarCollapse>
                </Navbar>
            </div>
        </MegaMenu>
    )
}